import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  alpha,
  useTheme,
  FormHelperText
} from '@mui/material';
import { BidFormErrors, EmailNotification } from './index';
import { httpRequest } from 'src/services/httpRequest';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

interface EmailNotificationFormProps {
  emailData: EmailNotification;
  setEmailData: React.Dispatch<React.SetStateAction<EmailNotification>>;
  goLiveDate: Date | null;
  errors: BidFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<BidFormErrors>>;
}

const EmailNotificationForm: React.FC<EmailNotificationFormProps> = ({
  emailData,
  setEmailData,
  goLiveDate,
  errors,
  setErrors
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const isGoLiveDateFilled = goLiveDate !== null && goLiveDate !== undefined;
  const theme = useTheme();

  // Fetch email template when component loads
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const result = await httpRequest(
          'GET',
          '/bid_portal/template/email?Form=CreateBid&Event=Create',
          {},
          {}
        );

        if (result?.status === 'success' && result.data?.data?.length > 0) {
          const template = result.data.data[0]; // take first template

          // Set the fetched template to emailData
          setEmailData((prev) => ({
            ...prev,
            subject: template.EmailSubject || '',
            content: (template.EmailBody || '').replace(/<br\s*\/?>/gi, '\n')
          }));

          // Set the editor state from the fetched HTML content
          const contentBlock = htmlToDraft(template.EmailBody || '');
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      } catch (err) {
        console.error('Error fetching email template:', err);
      }
    };

    fetchTemplate();
  }, [setEmailData]);

  // Convert editor state to HTML and set to emailData
  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const htmlContent = draftToHtml(
      convertToRaw(newEditorState.getCurrentContent())
    );
    setEmailData((prev) => ({
      ...prev,
      content: htmlContent
    }));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email Subject*"
          value={emailData.subject}
          error={!!errors.subject}
          helperText={errors.subject}
          onChange={(e) => {
            setEmailData({ ...emailData, subject: e.target.value });
            if (errors.subject && e.target.value.trim() !== '') {
              setErrors((prev) => ({ ...prev, subject: '' }));
            }
          }}
        />
      </Grid>

      {/* Replace TextField with Draft.js Editor */}
      <Grid item xs={12}>
        <Box
          sx={{
            border: `1px solid ${alpha(theme.palette.primary.main, 0.23)}`,
            borderRadius: 1,
            '& .rdw-editor-wrapper': {
              backgroundColor: 'transparent'
            },
            '& .rdw-editor-toolbar': {
              borderBottom: `1px solid ${alpha(
                theme.palette.primary.main,
                0.23
              )}`,
              borderRadius: '4px 4px 0 0',
              padding: '6px',
              backgroundColor: alpha(theme.palette.background.default, 0.5)
            },
            '& .rdw-editor-main': {
              padding: '12px',
              minHeight: '250px',
              fontSize: '14px',
              lineHeight: '1.5'
            },
            '& .rdw-option-wrapper': {
              border: 'none',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            },
            '& .rdw-option-active': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2)
            }
          }}
        >
          <Editor
            editorState={editorState}
            onEditorStateChange={handleEditorChange}
            placeholder="Enter email content..."
            toolbar={{
              options: ['inline', 'list', 'textAlign', 'history'],
              inline: {
                options: ['bold', 'italic', 'underline']
              },
              list: {
                options: ['unordered', 'ordered']
              },
              textAlign: {
                options: ['left', 'center', 'right']
              }
            }}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset" fullWidth error={!!errors.saveType}>
          <InputLabel>Notification Options*</InputLabel>
          <Select
            value={emailData.saveType}
            onChange={(e) => {
              setEmailData({
                ...emailData,
                saveType: e.target.value as EmailNotification['saveType']
              });
              if (errors.saveType && e.target.value.trim() !== '') {
                setErrors((prev) => ({ ...prev, saveType: '' }));
              }
            }}
            label="Notification Options*"
          >
            <MenuItem value="Save">Save</MenuItem>
            <MenuItem
              value="Save and Notify Later"
              disabled={!isGoLiveDateFilled}
            >
              Save and Notify Later
            </MenuItem>
            <MenuItem value="Save and Notify" disabled={isGoLiveDateFilled}>
              Save and Notify
            </MenuItem>
          </Select>
          {errors.saveType && (
            <FormHelperText>{errors.saveType}</FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default EmailNotificationForm;
